// 30-Day Demo Case (@slowcafe.kaily) — stays on /ig/content/member/demo.
// Auto-logs into the demo member account, then shows merchants exactly how
// they download content and post it to their own Instagram.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Download, Copy, Play, Instagram, ArrowRight, Clock, CheckCircle2, X } from "lucide-react";
import MemberShell from "@/components/MemberShell";
import { apiLogin, authHeaders, getToken } from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_META = {
    photo: { cn: "贴文 POST", en: "POST", cls: "bg-cafe-olive text-cafe-cream" },
    reel: { cn: "REEL 视频", en: "REEL", cls: "bg-cafe-espresso text-cafe-cream" },
    story: { cn: "限时动态 STORY", en: "STORY", cls: "bg-[#C9A227] text-cafe-espresso" },
};

const CAP_OPTS = [["cn", "中文"], ["en", "English"], ["bi", "双语 Bilingual"]];

export default function DemoMember() {
    const [phase, setPhase] = useState("checking"); // checking | login | busy | ready
    const [data, setData] = useState(null);
    const [sel, setSel] = useState(null);
    const [id, setId] = useState("");
    const [pw, setPw] = useState("");
    const [err, setErr] = useState("");
    const [ui, setUi] = useState("cn");
    const [capLang, setCapLang] = useState("bi");
    const T = (cn, en) => (ui === "en" ? en : cn);

    const fetchCase = async () => {
        const { data: dc } = await axios.get(`${API}/member/hub/demo-case`, { headers: authHeaders(), withCredentials: true });
        setData(dc);
        setPhase("ready");
    };

    useEffect(() => {
        (async () => {
            try { await fetchCase(); } catch { setPhase("login"); }
        })();
    }, []); // eslint-disable-line

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        setPhase("busy");
        try {
            const email = id.trim().includes("@") ? id.trim().toLowerCase() : `${id.trim().toLowerCase()}@slowcafe.my`;
            await apiLogin({ email, password: pw });
            await fetchCase();
        } catch {
            setErr("ID 或密码错误 · Wrong ID or password");
            setPhase("login");
        }
    };

    if (phase === "checking") return (
        <div className="min-h-screen bg-cafe-cream flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-cafe-olive" />
        </div>);

    if (phase === "login" || phase === "busy") return (
        <div className="min-h-screen bg-cafe-cream flex items-center justify-center px-4" data-testid="demo-login">
            <div className="w-full max-w-sm">
                <p className="text-[11px] uppercase tracking-[0.24em] text-cafe-olive font-bold text-center">slowcafe.kaily · Live Demo</p>
                <h1 className="font-serif text-3xl text-cafe-espresso text-center mt-2">IG Content AI — Live Demo</h1>
                <p className="text-[13px] text-cafe-mocha text-center mt-2 mb-8">See the 30-day content calendar merchants receive every month <span className="text-cafe-mocha/60">· 体验商家每月收到的内容日历</span></p>
                <form onSubmit={submit} className="grid gap-3">
                    <input value={id} onChange={(e) => setId(e.target.value)} data-testid="demo-id-input"
                        placeholder="Demo ID"
                        className="w-full rounded-xl border border-cafe-espresso/15 bg-white px-4 py-3 text-sm outline-none focus:border-cafe-olive" />
                    <input value={pw} onChange={(e) => setPw(e.target.value)} type="password" data-testid="demo-password-input"
                        placeholder="Password"
                        className="w-full rounded-xl border border-cafe-espresso/15 bg-white px-4 py-3 text-sm outline-none focus:border-cafe-olive" />
                    {err && <p className="text-[12.5px] text-red-700" data-testid="demo-login-error">{err}</p>}
                    <button type="submit" disabled={phase === "busy" || !id || !pw} data-testid="demo-login-submit"
                        className="rounded-full bg-cafe-olive hover:bg-cafe-oliveDark disabled:opacity-50 text-cafe-cream h-11 text-sm font-medium flex items-center justify-center gap-2">
                        {phase === "busy" && <Loader2 className="h-4 w-4 animate-spin" />} Log in to Demo
                    </button>
                </form>
                <p className="text-[11.5px] text-cafe-mocha/70 text-center mt-6">Demo account provided by slowcafe.kaily · WhatsApp us to get access</p>
            </div>
        </div>);

    if (!data) return (
        <div className="min-h-screen bg-cafe-cream flex items-center justify-center px-4">
            <div className="text-center">
                <p className="text-sm text-cafe-mocha">Demo unavailable right now · 演示暂时不可用</p>
                <button onClick={() => window.location.reload()} data-testid="demo-retry-btn"
                    className="mt-4 rounded-full bg-cafe-olive hover:bg-cafe-oliveDark text-cafe-cream px-6 py-2.5 text-sm font-medium">
                    Retry · 重试
                </button>
            </div>
        </div>);

    return (
        <MemberShell email="igdemo@slowcafe.my">
            <div data-testid="demo-case-page">
                {/* hero */}
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <p className="text-[11px] uppercase tracking-[0.24em] text-cafe-olive font-bold">Live Demo · 30-Day Content Calendar</p>
                    <div className="flex rounded-full border border-cafe-espresso/15 bg-white/70 p-1" data-testid="demo-ui-lang-toggle">
                        {[["cn", "中文"], ["en", "EN"]].map(([v, label]) => (
                            <button key={v} onClick={() => setUi(v)} data-testid={`demo-ui-${v}`}
                                className={`rounded-full px-4 py-1.5 text-[12px] font-semibold transition-colors ${ui === v ? "bg-cafe-espresso text-cafe-cream" : "text-cafe-mocha hover:text-cafe-espresso"}`}>
                                {label}
                            </button>))}
                    </div>
                </div>
                <h1 className="font-serif text-3xl sm:text-4xl text-cafe-espresso mt-2">
                    <a href={data.ig_url} target="_blank" rel="noreferrer" className="hover:text-cafe-olive transition-colors" data-testid="demo-ig-link">
                        @{data.ig_handle}
                    </a>{" "}{T("的 30 天内容日历", "— 30-Day Content Calendar")}
                </h1>
                <p className="text-sm text-cafe-mocha mt-3 max-w-2xl leading-relaxed">
                    {T("这就是你订阅后每月收到的东西——整整 30 天、排好日期的贴文/Reel/Story，配好图片、双语文案和 hashtag。点开任意一天，体验商家实际的发布流程。",
                        "This is what you receive every month — 30 scheduled posts, Reels & Stories with images, bilingual captions and hashtags. Open any day to try the real posting flow.")}
                </p>

                {/* 3-step guide */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-8" data-testid="demo-steps">
                    {[
                        ["1", T("点开任意一天", "Open any day"), T("查看当天的图片、文案和建议发布时间", "See the image, caption & suggested posting time")],
                        ["2", T("下载图片 · 复制文案", "Download image · Copy caption"), T("一个按钮下载，一个按钮复制——不用自己想内容", "One tap to download, one tap to copy — zero content stress")],
                        ["3", T("打开 IG 贴上发布", "Open IG & post"), T("新贴文 → 选图 → 粘贴文案 → 发布，30 秒搞定", "New post → pick image → paste caption → publish in 30s")],
                    ].map(([n, t, s]) => (
                        <div key={n} className="rounded-2xl border border-cafe-espresso/12 bg-white/70 p-5">
                            <div className="h-8 w-8 rounded-full bg-cafe-olive text-cafe-cream flex items-center justify-center text-sm font-bold">{n}</div>
                            <p className="font-semibold text-cafe-espresso text-[14px] mt-3">{t}</p>
                            <p className="text-[12.5px] text-cafe-mocha mt-1 leading-relaxed">{s}</p>
                        </div>))}
                </div>

                {/* caption language selector */}
                <div className="flex items-center gap-3 mt-10 flex-wrap" data-testid="demo-caption-lang">
                    <span className="text-[12px] uppercase tracking-[0.16em] text-cafe-mocha font-semibold">{T("文案语言", "Caption language")}:</span>
                    <div className="flex rounded-full border border-cafe-espresso/15 bg-white/70 p-1">
                        {CAP_OPTS.map(([v, label]) => (
                            <button key={v} onClick={() => setCapLang(v)} data-testid={`demo-cap-${v}`}
                                className={`rounded-full px-4 py-1.5 text-[12px] font-semibold transition-colors ${capLang === v ? "bg-cafe-olive text-cafe-cream" : "text-cafe-mocha hover:text-cafe-espresso"}`}>
                                {label}
                            </button>))}
                    </div>
                </div>

                {/* calendar grid */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mt-5">
                    {(data.days || []).map((d) => {
                        const tm = TYPE_META[d.type] || TYPE_META.photo;
                        const dt = ui === "en" ? (d.title_en || d.title) : d.title;
                        return (
                            <button key={d.day} onClick={() => setSel(d)} data-testid={`demo-day-card-${d.day}`}
                                className="text-left rounded-2xl overflow-hidden border border-cafe-espresso/12 bg-white/70 hover:border-cafe-olive hover:-translate-y-0.5 transition-all group">
                                {d.image_url ? (
                                    <div className="relative aspect-square overflow-hidden">
                                        <img src={d.image_url} alt={dt} loading="lazy"
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                                        {d.type === "reel" && (
                                            <div className="absolute inset-0 flex items-center justify-center">
                                                <div className="h-10 w-10 rounded-full bg-black/50 backdrop-blur flex items-center justify-center">
                                                    <Play className="h-4 w-4 text-white ml-0.5" fill="white" /></div>
                                            </div>)}
                                        <span className="absolute top-2 left-2 rounded-full bg-black/55 backdrop-blur text-white text-[10px] font-bold px-2 py-0.5">Day {d.day}</span>
                                    </div>
                                ) : (
                                    <div className="relative aspect-square bg-cafe-espresso flex items-center justify-center p-4">
                                        <p className="text-cafe-cream/90 text-[12px] leading-relaxed text-center font-serif">{dt}</p>
                                        <span className="absolute top-2 left-2 rounded-full bg-white/15 text-cafe-cream text-[10px] font-bold px-2 py-0.5">Day {d.day}</span>
                                    </div>
                                )}
                                <div className="p-2.5">
                                    <span className={`inline-block rounded-full text-[9.5px] font-bold px-2 py-0.5 ${tm.cls}`}>{tm[ui]}</span>
                                    <p className="text-[12px] text-cafe-espresso mt-1.5 leading-snug line-clamp-2">{dt}</p>
                                </div>
                            </button>);
                    })}
                </div>

                {/* footer CTA */}
                <div className="mt-12 rounded-2xl bg-cafe-espresso text-cafe-cream p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center gap-4">
                    <div className="flex-1">
                        <p className="font-serif text-xl">{T("想要这样的 30 天内容日历？", "Want a 30-day calendar like this?")}</p>
                        <p className="text-[13px] text-cafe-cream/70 mt-1">{T("一次性 Kit 或每月订阅 — 你的菜单、你的品牌声音，48 小时交付。", "One-time Kit or monthly subscription — your menu, your brand voice, delivered in 48 hours.")}</p>
                    </div>
                    <Link to="/ig/content/AI" data-testid="demo-offer-link"
                        className="inline-flex items-center gap-2 rounded-full bg-[#C9A227] text-cafe-espresso px-6 py-3 text-sm font-bold hover:brightness-110 shrink-0">
                        {T("查看方案与价格", "See plans & pricing")} <ArrowRight className="h-4 w-4" />
                    </Link>
                </div>
            </div>

            {sel && <DayDialog d={sel} ui={ui} capLang={capLang} onClose={() => setSel(null)} />}
        </MemberShell>
    );
}

function DayDialog({ d, ui, capLang, onClose }) {
    const tm = TYPE_META[d.type] || TYPE_META.photo;
    const [downloading, setDownloading] = useState(false);
    const [done, setDone] = useState({ img: false, copy: false });
    const T = (cn, en) => (ui === "en" ? en : cn);
    const dt = ui === "en" ? (d.title_en || d.title) : d.title;
    const capText = capLang === "cn" ? `${d.caption_cn}\n\n${d.hashtags}`
        : capLang === "en" ? `${d.caption_en}\n\n${d.hashtags}`
            : `${d.caption_cn}\n\n${d.caption_en}\n\n${d.hashtags}`;

    const downloadImg = async () => {
        setDownloading(true);
        try {
            const res = await fetch(d.image_url);
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url; a.download = `slowcafe-day${d.day}.jpeg`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
            setDone((s) => ({ ...s, img: true }));
            toast.success(T("图片已下载 — 打开 IG 就能直接选用", "Image downloaded — ready to pick in IG"));
        } catch {
            window.open(d.image_url, "_blank");
            setDone((s) => ({ ...s, img: true }));
        }
        setDownloading(false);
    };
    const copyCaption = () => {
        const text = capText;
        const ok = () => { setDone((s) => ({ ...s, copy: true })); toast.success(T("文案已复制 — 在 IG 发帖时直接粘贴", "Caption copied — paste it in your IG post")); };
        navigator.clipboard.writeText(text).then(ok).catch(() => {
            const ta = document.createElement("textarea");
            ta.value = text; document.body.appendChild(ta); ta.select();
            try { document.execCommand("copy"); ok(); } catch { toast.error(T("复制失败，请长按文案手动复制", "Copy failed — long-press the caption to copy manually")); }
            ta.remove();
        });
    };

    return (
        <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="demo-day-dialog">
            <div className="bg-cafe-cream rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                {d.image_url && (
                    <div className="relative">
                        <img src={d.image_url} alt={dt} className="w-full aspect-square object-cover rounded-t-2xl" />
                        {d.type === "reel" && (
                            <span className="absolute bottom-3 left-3 rounded-full bg-black/60 backdrop-blur text-white text-[11px] px-3 py-1 flex items-center gap-1.5 w-fit">
                                <Play className="h-3 w-3" fill="white" /> {T("Reel 示意封面 — 实际交付为可发布的视频文件", "Reel cover preview — actual delivery is a ready-to-post video")}
                            </span>)}
                        <button onClick={onClose} className="absolute top-3 right-3 h-8 w-8 rounded-full bg-black/50 text-white flex items-center justify-center" data-testid="demo-dialog-close">
                            <X className="h-4 w-4" /></button>
                    </div>)}
                <div className="p-5 sm:p-6">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className={`rounded-full text-[10px] font-bold px-2.5 py-1 ${tm.cls}`}>{tm[ui]}</span>
                        <span className="text-[12px] text-cafe-mocha flex items-center gap-1"><Clock className="h-3 w-3" /> {T(`建议 ${d.time} 发布`, `Best time: ${d.time}`)}</span>
                        {!d.image_url && (
                            <button onClick={onClose} className="ml-auto h-7 w-7 rounded-full bg-cafe-espresso/10 flex items-center justify-center" data-testid="demo-dialog-close-story">
                                <X className="h-3.5 w-3.5 text-cafe-espresso" /></button>)}
                    </div>
                    <h3 className="font-serif text-xl text-cafe-espresso mt-3">Day {d.day} · {dt}</h3>
                    <div className="rounded-xl bg-white border border-cafe-espresso/10 p-4 mt-4 space-y-3" data-testid="demo-caption-box">
                        {capLang !== "en" && <p className="text-[13.5px] text-cafe-espresso leading-relaxed whitespace-pre-wrap">{d.caption_cn}</p>}
                        {capLang !== "cn" && <p className={`leading-relaxed whitespace-pre-wrap ${capLang === "en" ? "text-[13.5px] text-cafe-espresso" : "text-[12.5px] text-cafe-mocha"}`}>{d.caption_en}</p>}
                        <p className="text-[12px] text-cafe-olive">{d.hashtags}</p>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-5">
                        {d.image_url && (
                            <button onClick={downloadImg} disabled={downloading} data-testid="demo-download-btn"
                                className="flex-1 min-w-[140px] rounded-full bg-cafe-olive hover:bg-cafe-oliveDark text-cafe-cream px-4 py-2.5 text-[13px] font-semibold flex items-center justify-center gap-2">
                                {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : done.img ? <CheckCircle2 className="h-4 w-4" /> : <Download className="h-4 w-4" />}
                                {done.img ? T("已下载", "Downloaded") : T("① 下载图片", "① Download image")}
                            </button>)}
                        <button onClick={copyCaption} data-testid="demo-copy-btn"
                            className="flex-1 min-w-[140px] rounded-full bg-cafe-espresso hover:opacity-90 text-cafe-cream px-4 py-2.5 text-[13px] font-semibold flex items-center justify-center gap-2">
                            {done.copy ? <CheckCircle2 className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                            {done.copy ? T("已复制", "Copied") : d.image_url ? T("② 复制文案", "② Copy caption") : T("① 复制文案", "① Copy caption")}
                        </button>
                    </div>
                    <div className="rounded-xl bg-cafe-latte/50 p-3.5 mt-4 flex items-start gap-2.5">
                        <Instagram className="h-4 w-4 text-cafe-olive shrink-0 mt-0.5" />
                        <p className="text-[12.5px] text-cafe-espresso leading-relaxed">
                            {d.type === "story"
                                ? (ui === "en"
                                    ? <>Last step: open Instagram → <b>Story</b> → type the text → publish ✓</>
                                    : <>最后一步：打开 Instagram → <b>限时动态 Story</b> → 输入文字 → 发布 ✓</>)
                                : (ui === "en"
                                    ? <>Last step: open Instagram → <b>New post</b> → pick the downloaded image → <b>paste caption</b> → publish ✓</>
                                    : <>最后一步：打开 Instagram → <b>新贴文</b> → 选择刚下载的图片 → <b>粘贴文案</b> → 发布 ✓</>)}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
